import { useState, useRef, useEffect, type ChangeEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Search } from "lucide-react";

import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export interface Country {
  code: string; // ISO-2, e.g. "RU"
  dial: string; // e.g. "+7"
  flag: string;
  name: string;
}

// Curated list of common Telegram markets. Users can search by country, ISO code or dial code.
export const COUNTRIES: Country[] = [
  { code: "RU", dial: "+7", flag: "🇷🇺", name: "Россия" },
  { code: "UA", dial: "+380", flag: "🇺🇦", name: "Украина" },
  { code: "BY", dial: "+375", flag: "🇧🇾", name: "Беларусь" },
  { code: "KZ", dial: "+7", flag: "🇰🇿", name: "Казахстан" },
  { code: "UZ", dial: "+998", flag: "🇺🇿", name: "Узбекистан" },
  { code: "KG", dial: "+996", flag: "🇰🇬", name: "Кыргызстан" },
  { code: "AM", dial: "+374", flag: "🇦🇲", name: "Армения" },
  { code: "AZ", dial: "+994", flag: "🇦🇿", name: "Азербайджан" },
  { code: "GE", dial: "+995", flag: "🇬🇪", name: "Грузия" },
  { code: "MD", dial: "+373", flag: "🇲🇩", name: "Молдова" },
  { code: "TJ", dial: "+992", flag: "🇹🇯", name: "Таджикистан" },
  { code: "TM", dial: "+993", flag: "🇹🇲", name: "Туркменистан" },
  { code: "LV", dial: "+371", flag: "🇱🇻", name: "Латвия" },
  { code: "LT", dial: "+370", flag: "🇱🇹", name: "Литва" },
  { code: "EE", dial: "+372", flag: "🇪🇪", name: "Эстония" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Польша" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Германия" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "Франция" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Италия" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "Испания" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Нидерланды" },
  { code: "CZ", dial: "+420", flag: "🇨🇿", name: "Чехия" },
  { code: "RS", dial: "+381", flag: "🇷🇸", name: "Сербия" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "Великобритания" },
  { code: "US", dial: "+1", flag: "🇺🇸", name: "США" },
  { code: "CA", dial: "+1", flag: "🇨🇦", name: "Канада" },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "Мексика" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Бразилия" },
  { code: "AR", dial: "+54", flag: "🇦🇷", name: "Аргентина" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Турция" },
  { code: "AE", dial: "+971", flag: "🇦🇪", name: "ОАЭ" },
  { code: "IL", dial: "+972", flag: "🇮🇱", name: "Израиль" },
  { code: "ID", dial: "+62", flag: "🇮🇩", name: "Индонезия" },
  { code: "IN", dial: "+91", flag: "🇮🇳", name: "Индия" },
  { code: "PK", dial: "+92", flag: "🇵🇰", name: "Пакистан" },
  { code: "BD", dial: "+880", flag: "🇧🇩", name: "Бангладеш" },
  { code: "TH", dial: "+66", flag: "🇹🇭", name: "Таиланд" },
  { code: "VN", dial: "+84", flag: "🇻🇳", name: "Вьетнам" },
  { code: "PH", dial: "+63", flag: "🇵🇭", name: "Филиппины" },
  { code: "MY", dial: "+60", flag: "🇲🇾", name: "Малайзия" },
  { code: "SG", dial: "+65", flag: "🇸🇬", name: "Сингапур" },
  { code: "CN", dial: "+86", flag: "🇨🇳", name: "Китай" },
  { code: "HK", dial: "+852", flag: "🇭🇰", name: "Гонконг" },
  { code: "KR", dial: "+82", flag: "🇰🇷", name: "Южная Корея" },
  { code: "JP", dial: "+81", flag: "🇯🇵", name: "Япония" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Австралия" },
  { code: "EG", dial: "+20", flag: "🇪🇬", name: "Египет" },
  { code: "MA", dial: "+212", flag: "🇲🇦", name: "Марокко" },
  { code: "NG", dial: "+234", flag: "🇳🇬", name: "Нигерия" },
  { code: "ZA", dial: "+27", flag: "🇿🇦", name: "ЮАР" },
];

interface PhoneInputProps {
  country: Country;
  onCountryChange: (c: Country) => void;
  phone: string;
  onPhoneChange: (p: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function PhoneInput({
  country,
  onCountryChange,
  phone,
  onPhoneChange,
  autoFocus = false,
  disabled = false,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const onPhoneInput = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 14);
    onPhoneChange(digits);
  };

  const filtered = COUNTRIES.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.dial.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <div
        className={cn(
          "flex items-stretch gap-2 h-[58px]",
          disabled && "opacity-60 pointer-events-none",
        )}
      >
        {/* Country chip */}
        <button
          onClick={() => {
            setOpen(true);
            haptic.select();
          }}
          className={cn(
            "flex items-center gap-2 px-3 rounded-2xl",
            "bg-surface border border-white/[0.08] hover:border-white/[0.14]",
            "transition-colors min-w-[112px]",
          )}
        >
          <span className="text-2xl leading-none">{country.flag}</span>
          <span className="text-base font-medium tabular-nums">{country.dial}</span>
          <ChevronDown className="size-4 text-text-dim ml-auto" />
        </button>

        {/* Phone input */}
        <div
          className={cn(
            "flex-1 rounded-2xl bg-surface border transition-colors relative",
            "focus-within:border-accent focus-within:shadow-[0_0_0_4px_rgba(255,107,53,0.12)]",
            "border-white/[0.08]",
          )}
        >
          <input
            ref={inputRef}
            type="tel"
            inputMode="numeric"
            placeholder="999 123 45 67"
            value={formatPhone(phone, country.code)}
            onChange={onPhoneInput}
            disabled={disabled}
            className={cn(
              "w-full h-full bg-transparent px-4",
              "text-base font-medium tabular-nums",
              "outline-none placeholder:text-text-faint",
            )}
          />
        </div>
      </div>

      {/* Country picker sheet */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className={cn(
                "fixed inset-x-0 bottom-0 top-12 z-[61]",
                "glass-strong rounded-t-[28px] overflow-hidden",
                "border-t border-white/[0.08] flex flex-col",
              )}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
            >
              <div className="flex items-center justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-white/15" />
              </div>
              <div className="px-6 pt-3 pb-4">
                <div className="text-xl font-semibold tracking-tight">Страна</div>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-dim" />
                  <input
                    autoFocus
                    placeholder="Поиск..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full h-11 bg-surface border border-white/[0.08] rounded-xl pl-10 pr-3 text-sm outline-none focus:border-accent transition-colors"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-8">
                {filtered.map((c) => {
                  const selected = c.code === country.code;
                  return (
                    <button
                      key={c.code}
                      onClick={() => {
                        onCountryChange(c);
                        setOpen(false);
                        haptic.impact("light");
                        setQuery("");
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-3 rounded-xl",
                        "hover:bg-white/[0.04] transition-colors",
                      )}
                    >
                      <span className="text-2xl">{c.flag}</span>
                      <span className="flex-1 text-left text-[15px]">{c.name}</span>
                      <span className="text-sm tabular-nums text-text-muted">{c.dial}</span>
                      {selected && <Check className="size-4 text-accent ml-2" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function formatPhone(digits: string, country: string): string {
  if (!digits) return "";
  // RU / KZ format: "999 123 45 67"
  if (country === "RU" || country === "KZ") {
    const m = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!m) return digits;
    return [m[1], m[2], m[3], m[4]].filter(Boolean).join(" ");
  }
  // UA / BY-style 3-3-2-2
  if (country === "UA" || country === "BY") {
    const m = digits.match(/^(\d{0,2})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!m) return digits;
    return [m[1], m[2], m[3], m[4]].filter(Boolean).join(" ");
  }
  // Default: 3-3-3-3 grouping
  return digits.replace(/(\d{3})(?=\d)/g, "$1 ");
}
